import { useState, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { pageVariants,  pageTransition} from "../constants/GlobalStyles";
import useAxios from '../hooks/useAxios';
import AuthContext from '../contexts/auth-context';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Stack,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
// import HealingIcon from '@mui/icons-material/Healing';
import MachineLearningPageStyle from './MachineLearning.module';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
// import FormLabel from '@mui/material/FormLabel';
// import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

const EXAMPLE_TEXT = "a seventy year old Hispanic female patient with a history of osteoporosis, rheumatoid arthritis, and hypertension and anemiab. She presented to the outpatient clinic with complaints of joint pain, stiffness, and difficulty walking.";

const MachineLearningPage = () => {
  const [data, setData] = useState({ field1: "" });
  const [snoicd, setSnoicd] = useState("icd10");
  const [response, setResponse] = useState('');
  const [error, setError] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedEntities, setExpandedEntities] = useState({});
  const { operation } = useAxios();
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const classes = MachineLearningPageStyle();

  const handleChange = (e) => {
    setData({ field1: e.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(undefined);
    
    await operation({
      method: 'POST',
      url: '/api/patient/encounter/infer/create',
      headers: { Authorization: `token ${authCtx.token}` },
      data: {
        content: data.field1,
        // snoicd: snoicd,
        response: snoicd
      }
    }, setResponse, setError);
    
    setIsLoading(false);
  };

  const toggleEntity = (entityId) => {
    setExpandedEntities(prev => ({
      ...prev,
      [entityId]: !prev[entityId]
    }));
  };

  // const getCategoryIcon = (category) => {
  //   switch (category) {
  //     case 'MEDICAL_CONDITION':
  //       return <MedicalServicesIcon />;
  //     case 'SYMPTOM':
  //       return <HealingIcon />;
  //     case 'ANATOMY':
  //       return <MonitorHeartIcon />;
  //     default:
  //       return '-';
  //   }
  // };

  const renderEntityCard = (entity) => {
    const isExpanded = expandedEntities[entity.Id];
    const confidenceScore = Math.round(entity.Score * 100);
    // const isDiagnosis = entity.Traits.some(trait => trait.Name === 'DIAGNOSIS');
    // const isSymptom = entity.Traits.some(trait => trait.Name === 'SYMPTOM');

    // Determine card style based on confidence score
    const getCardStyle = () => {
      if (confidenceScore > 80) return classes.highConfidenceCard;
      if (confidenceScore > 60) return classes.mediumConfidenceCard;
      return classes.lowConfidenceCard;
    };

    return (
      <Card key={entity.Id} sx={getCardStyle()}>
        <CardHeader
          // avatar={getCategoryIcon(entity.Category)}
          action={
            <IconButton onClick={() => toggleEntity(entity.Id)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{fontSize: '20px'}}>{entity.Text}</Typography>
              <Chip
                size="small"
                label={`${confidenceScore}% confidence`}
                color={confidenceScore > 80 ? 'success' : confidenceScore > 60 ? 'warning' : 'error'}
              />
            </Stack>
            
          }
          // subheader={
          //   <Stack direction="row" spacing={1}>
          //     {entity.Traits.map((trait, index) => (
          //       <Chip
          //         key={index}
          //         size="small"
          //         label={`Traits: ${trait.Name}`}
          //         color={trait.Name === 'DIAGNOSIS' ? 'primary' : 'secondary'}
          //         variant="outlined"
          //       />
          //     ))}
          //   </Stack>
          // }
        />
        <Collapse in={isExpanded}>
          <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Attributes:
                </Typography>
                {entity.Attributes && entity.Attributes.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{margin: '10px 0'}}>
                  {entity.Traits.map((trait, index) => (
                  <Chip
                    key={index}
                    size="small"
                    label={`Traits: ${trait.Name}`}
                    color={trait.Name === 'DIAGNOSIS' ? 'primary' : 'secondary'}
                    variant="outlined"
                  />
                ))}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {entity.Attributes.map((attr, index) => (
                    <Chip
                      key={index}
                      size="small"
                      label={`${attr.Type}: ${attr.Text}`}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            )}
            <List dense>
            <Typography sx={{fontWeight: '500'}}>Key identified concepts</Typography>

              {entity.ICD10CMConcepts && entity.ICD10CMConcepts.map((concept, index) => (
                <ListItem key={index}>
                  
                  <ListItemText
                  
                    primary={<Typography sx={{ fontWeight: '400'}}>{concept.Description}</Typography>}
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                        sx={{fontWeight: '600'}}
                          size="small"
                          label={`${concept.Code}`}
                          color="primary"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{fontWeight: '500'}}>
                          {Math.round(concept.Score * 100)}% match
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
              {entity.SNOMEDCTConcepts && entity.SNOMEDCTConcepts.map((concept, index) => (
                <ListItem key={index}>
                  <ListItemText
                  
                    primary={<Typography sx={{ fontWeight: '400'}}>{concept.Description}</Typography>}
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                        sx={{fontWeight: '600'}}
                          size="small"
                          label={`${concept.Code}`}
                          color="primary"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{fontWeight: '500'}}>
                          {Math.round(concept.Score * 100)}% match
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
    >
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
        <IconButton
          size="small"
          sx={classes.arrow_button}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon fontSize="small" sx={classes.arrow_icon} />
        </IconButton>

        <Typography sx={classes.title}>
          Medical Text Analysis
        </Typography>

        {/* Example Text Section */}
        <Box sx={classes.exampleCard}>
          <Typography sx={classes.exampleTitle}>
            Example Text
          </Typography>
          <Typography sx={classes.exampleText}>
            {EXAMPLE_TEXT}
          </Typography>
        </Box>

        {/* Input Section */}
        <Box sx={classes.inputCard}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={6}
              variant="outlined"
              label="Enter medical text for analysis"
              placeholder="Enter the medical text you want to analyze..."
              onChange={handleChange}
              sx={[{ mb: 2 }, classes.textField]}
            />
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={snoicd}
                onChange={(e) => setSnoicd(e.target.value)}
              >
                <FormControlLabel value="icd10" control={<Radio />} label="ICD-10-CM" />
                <FormControlLabel value="snomed" control={<Radio />} label="SNOMED" />
              </RadioGroup>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              disabled={isLoading || !data.field1.trim()}
              startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
              fullWidth
            >
              {isLoading ? 'Analyzing...' : 'Analyze Text'}
            </Button>
          </form>
        </Box>

        {/* Results Section */}
        {error ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            An error occurred while analyzing the text. Please try again.
          </Alert>
        ) : response ? (
          <Box>
            <Typography sx={classes.resultsTitle}>
              Analysis Results ({response.response.Entities.length})
            </Typography>
            <Grid container spacing={3}>
              {response.response.Entities.map((entity, index) => (
                <Grid item xs={12} sm={6} key={entity.Id}>
                  {renderEntityCard(entity)}
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box sx={classes.emptyState}>
            <Typography color="text.secondary">
              Enter text and click "Analyze" to see results
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
    </motion.div>
  );
};

export default MachineLearningPage;
